import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Card, CardContent, Typography, Grid, TextField, Button,
    FormControl, InputLabel, Select, MenuItem, Divider, Alert,
    InputAdornment, Chip, IconButton, CircularProgress, Pagination,
    Avatar, Badge, Tooltip, Dialog, DialogTitle, DialogContent,
    DialogActions, List, ListItem, ListItemText, ListItemSecondaryAction,
    Paper, Stack,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon, Save as SaveIcon,
    Search as SearchIcon, Add as AddIcon, Delete as DeleteIcon,
    ShoppingCart as CartIcon, Inventory as InventoryIcon,
    CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { fetchProductsPage, setSearch, clearProducts } from '../../../redux/slices/productsSlice';
import orderService from '../../../services/orderService';
import customerService from '../../../services/customerService';
import productService from '../../../services/productService';
import promotionService from '../../../services/promotionService';
import storeService from '../../../services/storeService';
import { useCurrency } from '../../../hooks/useCurrency';

// ─────────────────────────────────────────────────────────────────────────────

const OrderForm = () => {
    const navigate = useNavigate();
    const { id } = useParams(); // present only on /orders/:id/edit — editing an existing DRAFT
    const isEditMode = Boolean(id);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    // One system-wide currency (Paramètres › Devise & Système): shown read-only, never picked per order.
    const { currency: systemCurrency, currencySymbol, format: fmtMoney } = useCurrency();
    const { user } = useSelector((state) => state.auth);
    const { items: products, totalPages, currentPage, pageSize, search, loading: productsLoading } =
        useSelector((state) => state.products);

    const isSuperAdminWithoutStore = user?.globalRole === 'SUPER_ADMIN' && !user?.store;

    // Order meta
    const [formData, setFormData] = useState({
        storeId: '',
        storeName: '',
        customerId: '',
        paymentMethod: 'CASH',
        shippingAddress: '',
        billingAddress: '',
        taxAmount: '',
        shippingAmount: '',
        manualDiscountAmount: '',
        notes: '',
    });

    // Cart: array of { product, variantId, variantName, variantPrice, quantity, promotionId, stock }
    const [cartItems, setCartItems] = useState([]);

    // Supporting data
    const [stores, setStores] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    // Only relevant in edit mode — blocks the form while the existing draft
    // loads, and again if it turns out not to be editable (not a DRAFT
    // admin order — see OrderService#updateDraftOrder's own guard).
    const [loadingOrder, setLoadingOrder] = useState(isEditMode);

    // Variant picker dialog
    const [variantDialog, setVariantDialog] = useState({ open: false, product: null, variants: [] });
    const [variantLoading, setVariantLoading] = useState(false);

    // Local search state (debounced before dispatch)
    const [searchInput, setSearchInput] = useState('');

    // ── Init ──────────────────────────────────────────────────────────────────

    useEffect(() => {
        if (isSuperAdminWithoutStore) storeService.getAllStores().then(s => setStores(s || [])).catch(() => {});
        customerService.getCustomers().then(c => setCustomers((c || []).filter(x => x.isActive !== false))).catch(() => {});
        promotionService.getPromotions().then(p => setPromotions(p || [])).catch(() => {});

        // Load first page of products
        dispatch(fetchProductsPage({ page: 0, size: pageSize, search: '' }));

        return () => {
            dispatch(clearProducts());
        };
    }, []);

    // ── Edit mode: load the existing draft order's details/items ────────────────

    useEffect(() => {
        if (!isEditMode) return;
        orderService.getOrderById(id).then((order) => {
            if (order.source !== 'ADMIN' || order.status !== 'DRAFT') {
                toast.error(t('orders_ext.notEditable'));
                navigate(`/orders/${id}`);
                return;
            }
            setFormData({
                storeId: order.storeId || '',
                storeName: order.storeName || '',
                customerId: order.customerId || '',
                paymentMethod: order.paymentMethod || 'CASH',
                shippingAddress: order.shippingAddress || '',
                billingAddress: order.billingAddress || '',
                taxAmount: order.taxAmount ? String(order.taxAmount) : '',
                shippingAmount: order.shippingAmount ? String(order.shippingAmount) : '',
                manualDiscountAmount: order.manualDiscountAmount ? String(order.manualDiscountAmount) : '',
                notes: order.notes || '',
            });
            setCartItems((order.items || []).map((item) => ({
                // No nested product object comes back on an order item
                // (OrderItemDTO is flat) — only its id is used elsewhere
                // for the catalog's "already in cart" highlight, which is
                // a cosmetic-only loss here.
                product: { id: undefined, name: item.productName },
                variantId: item.variantId,
                variantName: item.variantName,
                variantPrice: item.unitPrice,
                quantity: item.quantity,
                promotionId: item.promotionId || '',
                // Existing reserved items aren't re-checked against live
                // stock on load (only newly added ones are, via the variant
                // dialog) — this order's own reservation already accounts
                // for them.
                stock: Infinity,
            })));
        }).catch(() => {
            toast.error(t('orders.fetchError'));
            navigate('/orders');
        }).finally(() => setLoadingOrder(false));
    }, [id]);

    // ── Search debounce ───────────────────────────────────────────────────────

    useEffect(() => {
        const timer = setTimeout(() => {
            dispatch(setSearch(searchInput));
            dispatch(fetchProductsPage({ page: 0, size: pageSize, search: searchInput }));
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // ── Page change: loads new page, previous page products are removed from store ──

    const handlePageChange = useCallback((_, newPage) => {
        // newPage is 1-based from MUI Pagination
        dispatch(fetchProductsPage({ page: newPage - 1, size: pageSize, search }));
    }, [dispatch, pageSize, search]);

    // ── Variant dialog ────────────────────────────────────────────────────────

    const openVariantDialog = async (product) => {
        setVariantDialog({ open: true, product, variants: [] });
        setVariantLoading(true);
        try {
            const variants = await productService.getActiveProductVariants(product.id);
            setVariantDialog(prev => ({ ...prev, variants: variants || [] }));
        } catch {
            toast.error(t('common.loadError'));
        } finally {
            setVariantLoading(false);
        }
    };

    const selectVariant = (variant) => {
        const product = variantDialog.product;
        const existing = cartItems.findIndex(i => i.variantId === variant.id);
        if (existing >= 0) {
            // Increment quantity
            setCartItems(prev => prev.map((item, i) =>
                i === existing ? { ...item, quantity: item.quantity + 1 } : item
            ));
        } else {
            setCartItems(prev => [...prev, {
                product,
                variantId: variant.id,
                variantName: variant.name,
                variantPrice: parseFloat(variant.price) || 0,
                quantity: 1,
                promotionId: '',
                stock: (variant.stockQuantity ?? 0) - (variant.reservedQuantity ?? 0),
            }]);
        }
        setVariantDialog({ open: false, product: null, variants: [] });
        toast.success(`${product.name} — ${variant.name}`, { autoClose: 1500 });
    };

    // ── Cart helpers ──────────────────────────────────────────────────────────

    const removeCartItem = (idx) => setCartItems(prev => prev.filter((_, i) => i !== idx));

    const updateCartItem = (idx, field, value) =>
        setCartItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));

    // Mirrors the backend's PromotionCalculationService#calculateDiscountedPrice —
    // display-only (the server always recomputes authoritatively from
    // promotionId at order creation), but needs the same math so the total
    // shown here while building the order isn't just the promotion-free price.
    const getLineDiscount = (item) => {
        if (!item.promotionId) return 0;
        const promo = promotions.find(p => p.id === item.promotionId);
        if (!promo) return 0;
        const qty = parseInt(item.quantity) || 0;
        const lineTotal = item.variantPrice * qty;
        if (promo.type === 'PERCENTAGE') {
            return lineTotal * (Number(promo.value) / 100);
        }
        if (promo.type === 'FIXED_AMOUNT') {
            // Taken off each unit (matches the backend), capped at the unit price.
            return Math.min(Number(promo.value) || 0, item.variantPrice) * qty;
        }
        if (promo.type === 'BUY_X_GET_Y') {
            const buyQty = Number(promo.buyQuantity) || 1;
            const getQty = Number(promo.getQuantity) || 0;
            const bucket = buyQty + getQty;
            const freeItems = bucket > 0 ? Math.floor(qty / bucket) * getQty : 0;
            return freeItems * item.variantPrice;
        }
        return 0;
    };

    const getLineTotal = (item) => {
        const gross = item.variantPrice * (parseInt(item.quantity) || 0);
        return Math.max(gross - getLineDiscount(item), 0);
    };
    const getSubtotal = () => cartItems.reduce((s, item) => s + getLineTotal(item), 0);
    // Manual order-level remise, clamped to [0, subtotal] — the backend
    // enforces the same rule; clamping here keeps the live total honest.
    const getManualDiscount = () =>
        Math.min(Math.max(parseFloat(formData.manualDiscountAmount) || 0, 0), getSubtotal());
    const getTotalAmount = () => {
        return getSubtotal()
            - getManualDiscount()
            + (parseFloat(formData.taxAmount) || 0)
            + (parseFloat(formData.shippingAmount) || 0);
    };

    const fmt = (amount) => fmtMoney(amount);

    // ── Submit ────────────────────────────────────────────────────────────────

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isEditMode && isSuperAdminWithoutStore && !formData.storeId) {
            toast.error(t('common.saveError')); return;
        }
        if (cartItems.length === 0) {
            toast.error(t('common.saveError')); return;
        }

        if ((parseFloat(formData.manualDiscountAmount) || 0) > getSubtotal()) {
            toast.error('La remise ne peut pas dépasser le sous-total.'); return;
        }

        setSubmitting(true);
        try {
            const payload = {
                storeId: formData.storeId || null, // ignored server-side when editing — the draft's store never changes
                customerId: formData.customerId || null,
                currencyCode: systemCurrency,
                paymentMethod: formData.paymentMethod || null,
                shippingAddress: formData.shippingAddress || null,
                billingAddress: formData.billingAddress || null,
                taxAmount: formData.taxAmount ? parseFloat(formData.taxAmount) : null,
                shippingAmount: formData.shippingAmount ? parseFloat(formData.shippingAmount) : null,
                manualDiscountAmount: formData.manualDiscountAmount ? parseFloat(formData.manualDiscountAmount) : null,
                notes: formData.notes || null,
                items: cartItems.map(item => ({
                    variantId: item.variantId,
                    quantity: parseInt(item.quantity),
                    promotionId: item.promotionId || null,
                })),
            };
            const order = isEditMode
                ? await orderService.updateDraftOrder(id, payload)
                : await orderService.createOrder(payload);
            toast.success(isEditMode ? t('orders.updateSuccess') : t('orders.createSuccess'));
            navigate(`/orders/${order.id}`);
        } catch (err) {
            toast.error(err.response?.data?.message || t('common.saveError'));
        } finally {
            setSubmitting(false);
        }
    };

    // Mirrors the backend's Promotion#isCurrentlyApplicable() (also what
    // OrderService#applyItemsToOrder now enforces server-side) — isActive
    // alone isn't enough, an expired/not-yet-started/exhausted promotion
    // shouldn't even be offered in the dropdown, so a submit doesn't fail
    // with an error for something the admin had no way to see coming.
    const isPromotionCurrentlyApplicable = (p) => {
        if (!p.isActive) return false;
        const now = new Date();
        if (p.startDate && new Date(p.startDate) > now) return false;
        if (p.endDate && new Date(p.endDate) < now) return false;
        if (p.usageLimit != null && p.usageCount != null && p.usageCount >= p.usageLimit) return false;
        return true;
    };

    const activePromotions = promotions.filter(isPromotionCurrentlyApplicable);

    // ─────────────────────────────────────────────────────────────────────────

    if (loadingOrder) {
        return <Box sx={{ p: 3 }}><Typography>{t('common.loading')}</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(isEditMode ? `/orders/${id}` : '/orders')}>
                    {t('common.back')}
                </Button>
                <Typography variant="h4" fontWeight="bold">
                    {isEditMode ? t('orders.editOrder') : t('orders.createOrder')}
                </Typography>
                {cartItems.length > 0 && (
                    <Chip
                        icon={<CartIcon />}
                        label={`${cartItems.length}`}
                        color="primary"
                    />
                )}
            </Box>

            <form onSubmit={handleSubmit}>
                {/* ── Order Details ─────────────────────────────────── */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>{t('orders.order')}</Typography>
                        <Grid container spacing={2}>
                            {/* Store is fixed once a draft exists (see OrderService#updateDraftOrder) — shown read-only instead of the picker. */}
                            {isEditMode ? (
                                <Grid item xs={12} md={4}>
                                    <TextField fullWidth disabled label={t('roles.store')} value={formData.storeName} />
                                </Grid>
                            ) : isSuperAdminWithoutStore && (
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth required>
                                        <InputLabel>{t('roles.store')}</InputLabel>
                                        <Select value={formData.storeId} label={t('roles.store')}
                                            onChange={(e) => setFormData(p => ({ ...p, storeId: e.target.value }))}>
                                            {stores.map(s => (
                                                <MenuItem key={s.id} value={s.id}>{s.name} ({s.code})</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    {/* shrink + displayEmpty: "Client passant" is value="" — without
                                        displayEmpty, MUI treats an empty-string selection as "nothing
                                        selected" and renders the box blank instead of that label (looks
                                        exactly like the click didn't register); without shrink, the
                                        floating label overlaps that blank/short content. */}
                                    <InputLabel shrink>{t('orders.customer')}</InputLabel>
                                    <Select displayEmpty value={formData.customerId} label={t('orders.customer')}
                                        onChange={(e) => setFormData(p => ({ ...p, customerId: e.target.value }))}>
                                        <MenuItem value="">{t('orders.walkIn')}</MenuItem>
                                        {customers.map(c => (
                                            <MenuItem key={c.id} value={c.id}>
                                                {c.firstName} {c.lastName}{c.phone ? ` — ${c.phone}` : ''}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('orders.paymentMethod')}</InputLabel>
                                    <Select value={formData.paymentMethod} label={t('orders.paymentMethod')}
                                        onChange={(e) => setFormData(p => ({ ...p, paymentMethod: e.target.value }))}>
                                        <MenuItem value="CASH">{t('orders_ext.cashPayment')}</MenuItem>
                                        <MenuItem value="CARD">{t('orders_ext.cardPayment')}</MenuItem>
                                        <MenuItem value="TRANSFER">{t('orders_ext.transferPayment')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField fullWidth disabled label={t('common.currency')}
                                    value={`${systemCurrency} (${currencySymbol})`} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label={t('orders.taxAmount')} type="number"
                                    value={formData.taxAmount}
                                    onChange={(e) => setFormData(p => ({ ...p, taxAmount: e.target.value }))}
                                    InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
                                    inputProps={{ min: 0, step: 'any' }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField fullWidth label={t('orders.shippingAmount')} type="number"
                                    value={formData.shippingAmount}
                                    onChange={(e) => setFormData(p => ({ ...p, shippingAmount: e.target.value }))}
                                    InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
                                    inputProps={{ min: 0, step: 'any' }} />
                            </Grid>

                            {/* Manual discount moved to the Cart summary, right after
                                Sous-total — see below (matches the "Sous-total / Remise /
                                Total" layout, and it's meaningless without the subtotal
                                next to it). */}

                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label={t('orders.shippingAddress')} multiline rows={2}
                                    value={formData.shippingAddress}
                                    onChange={(e) => setFormData(p => ({ ...p, shippingAddress: e.target.value }))} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label={t('orders.billingAddress')} multiline rows={2}
                                    value={formData.billingAddress}
                                    onChange={(e) => setFormData(p => ({ ...p, billingAddress: e.target.value }))} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label={t('common.notes')} multiline rows={2}
                                    value={formData.notes}
                                    onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* ── Two-column: Product catalog + Cart ──────────��──── */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Product Catalog */}
                    <Grid item xs={12} lg={7}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <InventoryIcon color="primary" />
                                    <Typography variant="h6">{t('orders_ext.productCatalog')}</Typography>
                                </Box>

                                {/* Search */}
                                <TextField
                                    fullWidth
                                    placeholder={t('orders_ext.searchProductsPlaceholder')}
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: productsLoading && (
                                            <InputAdornment position="end">
                                                <CircularProgress size={18} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ mb: 2 }}
                                />

                                {/* Product list */}
                                {!productsLoading && products.length === 0 ? (
                                    <Alert severity="info">
                                        {searchInput
                                            ? t('orders_ext.noProductsFoundFor', { search: searchInput })
                                            : t('orders_ext.noProductsFound')}
                                    </Alert>
                                ) : (
                                    <Box>
                                        {products.filter((p) => p.isActive !== false).map((product) => {
                                            const inCart = cartItems.some(i => i.product?.id === product.id);
                                            return (
                                                <Paper
                                                    key={product.id}
                                                    variant="outlined"
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        p: 1.5,
                                                        mb: 1,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.15s',
                                                        border: inCart ? '1.5px solid' : undefined,
                                                        borderColor: inCart ? 'primary.main' : undefined,
                                                        '&:hover': { bgcolor: 'action.hover' },
                                                    }}
                                                    onClick={() => openVariantDialog(product)}
                                                >
                                                    <Avatar
                                                        src={product.mainImageUrl}
                                                        sx={{ width: 44, height: 44, bgcolor: 'primary.light' }}
                                                    >
                                                        {product.name?.charAt(0).toUpperCase()}
                                                    </Avatar>

                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body1" fontWeight="medium" noWrap>
                                                            {product.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            SKU: {product.sku || '—'} ·{' '}
                                                            {t('orders_ext.variantsCount', { count: product.variantCount ?? 0 })}
                                                        </Typography>
                                                    </Box>

                                                    <Box sx={{ textAlign: 'right', minWidth: 80 }}>
                                                        <Typography variant="body2" fontWeight="bold" color="primary">
                                                            {fmt(product.lowestPrice ?? 0)}
                                                        </Typography>
                                                        <Chip
                                                            label={`Stock: ${product.totalStock ?? 0}`}
                                                            size="small"
                                                            color={(product.totalStock ?? 0) > 0 ? 'success' : 'error'}
                                                            variant="outlined"
                                                            sx={{ fontSize: 10 }}
                                                        />
                                                    </Box>

                                                    {inCart ? (
                                                        <Tooltip title={t('orders_ext.alreadyInCart')}>
                                                            <CheckIcon color="primary" />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title={t('orders_ext.addToOrder')}>
                                                            <AddIcon color="action" />
                                                        </Tooltip>
                                                    )}
                                                </Paper>
                                            );
                                        })}
                                    </Box>
                                )}

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Pagination
                                            count={totalPages}
                                            page={currentPage + 1}
                                            onChange={handlePageChange}
                                            color="primary"
                                            size="small"
                                            showFirstButton
                                            showLastButton
                                        />
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Cart */}
                    <Grid item xs={12} lg={5}>
                        <Card sx={{ position: { lg: 'sticky' }, top: { lg: 80 } }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <Badge badgeContent={cartItems.length} color="primary">
                                        <CartIcon color="primary" />
                                    </Badge>
                                    <Typography variant="h6">{t('orders_ext.cart')}</Typography>
                                </Box>

                                {cartItems.length === 0 ? (
                                    <Alert severity="info" icon={false} sx={{ textAlign: 'center' }}>
                                        {t('orders_ext.emptyCartHint')}
                                    </Alert>
                                ) : (
                                    <>
                                        {cartItems.map((item, idx) => (
                                            <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight="medium" noWrap>
                                                            {item.product?.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" noWrap>
                                                            {item.variantName} · {fmt(item.variantPrice)}
                                                        </Typography>
                                                        {item.stock <= 0 && (
                                                            <Chip label={t('orders_ext.outOfStock')} size="small" color="error" sx={{ ml: 0.5 }} />
                                                        )}
                                                        {item.stock > 0 && item.stock < 5 && (
                                                            <Chip label={t('orders_ext.onlyLeft', { count: item.stock })} size="small" color="warning" sx={{ ml: 0.5 }} />
                                                        )}
                                                    </Box>
                                                    <IconButton size="small" color="error" onClick={() => removeCartItem(idx)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>

                                                <Grid container spacing={1} alignItems="center">
                                                    <Grid item xs={5}>
                                                        <TextField
                                                            fullWidth size="small" type="number" label={t('common.qty')}
                                                            value={item.quantity}
                                                            onChange={(e) => updateCartItem(idx, 'quantity', e.target.value)}
                                                            inputProps={{ min: 1, max: item.stock || 9999 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={7}>
                                                        <FormControl fullWidth size="small">
                                                            {/* Same displayEmpty/shrink fix as the customer select above — "Aucune" is value="". */}
                                                            <InputLabel shrink>{t('promotions.promotion')}</InputLabel>
                                                            <Select displayEmpty value={item.promotionId} label={t('promotions.promotion')}
                                                                onChange={(e) => updateCartItem(idx, 'promotionId', e.target.value)}>
                                                                <MenuItem value="">{t('common.none')}</MenuItem>
                                                                {activePromotions.map(p => (
                                                                    <MenuItem key={p.id} value={p.id}>
                                                                        {p.name}{p.code ? ` (${p.code})` : ''}
                                                                        {' — '}
                                                                        {p.promotionLabel}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>

                                                {(() => {
                                                    const promo = item.promotionId ? promotions.find(p => p.id === item.promotionId) : null;
                                                    if (promo?.type !== 'BUY_X_GET_Y') return null;
                                                    return (
                                                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                                            {t('orders_ext.buyXGetYHint', { buy: promo.buyQuantity, get: promo.getQuantity })}
                                                        </Typography>
                                                    );
                                                })()}

                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                    {getLineDiscount(item) > 0 && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                            {fmt(item.variantPrice * (parseInt(item.quantity) || 0))}
                                                        </Typography>
                                                    )}
                                                    <Chip
                                                        label={t('orders_ext.lineChip', { amount: fmt(getLineTotal(item)) })}
                                                        size="small"
                                                        color={getLineDiscount(item) > 0 ? 'success' : 'primary'}
                                                        variant="outlined"
                                                    />
                                                </Box>
                                            </Paper>
                                        ))}

                                        {/* Totals */}
                                        <Divider sx={{ my: 1.5 }} />
                                        <Stack spacing={0.5}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">{t('orders.subtotal')}</Typography>
                                                <Typography variant="body2">{fmt(getSubtotal())}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t('orders.manualDiscount')}
                                                </Typography>
                                                <TextField
                                                    variant="standard"
                                                    type="number"
                                                    value={formData.manualDiscountAmount}
                                                    onChange={(e) => setFormData(p => ({ ...p, manualDiscountAmount: e.target.value }))}
                                                    error={(parseFloat(formData.manualDiscountAmount) || 0) > getSubtotal()}
                                                    placeholder="0"
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        endAdornment: <InputAdornment position="end">-{currencySymbol}</InputAdornment>,
                                                    }}
                                                    inputProps={{
                                                        min: 0,
                                                        max: getSubtotal(),
                                                        step: 'any',
                                                        style: { textAlign: 'right', padding: 0 },
                                                    }}
                                                    sx={{
                                                        maxWidth: 150,
                                                        '& .MuiInputBase-root': { fontSize: '1rem' },
                                                        '& .MuiInputBase-input': { color: 'success.main' },
                                                        // Hide the native number-input spinner (Chrome/Safari/Edge + Firefox).
                                                        '& input[type=number]': { MozAppearance: 'textfield' },
                                                        '& input[type=number]::-webkit-outer-spin-button': {
                                                            WebkitAppearance: 'none',
                                                            margin: 0,
                                                        },
                                                        '& input[type=number]::-webkit-inner-spin-button': {
                                                            WebkitAppearance: 'none',
                                                            margin: 0,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            {(parseFloat(formData.manualDiscountAmount) || 0) > getSubtotal() && (
                                                <Typography variant="caption" color="error" sx={{ textAlign: 'right' }}>
                                                    {t('orders.manualDiscountHelper')}
                                                </Typography>
                                            )}
                                            {formData.taxAmount && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">{t('orders.taxAmount')}</Typography>
                                                    <Typography variant="body2">{fmt(parseFloat(formData.taxAmount) || 0)}</Typography>
                                                </Box>
                                            )}
                                            {formData.shippingAmount && (
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <Typography variant="body2" color="text.secondary">{t('orders.shippingAmount')}</Typography>
                                                    <Typography variant="body2">{fmt(parseFloat(formData.shippingAmount) || 0)}</Typography>
                                                </Box>
                                            )}
                                            <Divider />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="subtitle1" fontWeight="bold">{t('common.total')}</Typography>
                                                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                                                    {fmt(getTotalAmount())}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Submit */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" startIcon={<SaveIcon />}
                        disabled={submitting || cartItems.length === 0} size="large">
                        {submitting ? t('common.saving') : (isEditMode ? t('common.save') : t('orders.createOrder'))}
                    </Button>
                    <Button variant="outlined" size="large" onClick={() => navigate(isEditMode ? `/orders/${id}` : '/orders')}>
                        {t('common.cancel')}
                    </Button>
                </Box>
            </form>

            {/* Variant picker dialog */}
            <Dialog open={variantDialog.open} onClose={() => setVariantDialog({ open: false, product: null, variants: [] })}
                maxWidth="sm" fullWidth>
                <DialogTitle>
                    {t('orders_ext.selectVariantDialogTitle', { name: variantDialog.product?.name })}
                </DialogTitle>
                <DialogContent dividers>
                    {variantLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : variantDialog.variants.length === 0 ? (
                        <Alert severity="warning">{t('orders_ext.noActiveVariants')}</Alert>
                    ) : (
                        <List disablePadding>
                            {variantDialog.variants.map((v) => {
                                const avail = (v.stockQuantity ?? 0) - (v.reservedQuantity ?? 0);
                                return (
                                    <ListItem
                                        key={v.id}
                                        button
                                        onClick={() => selectVariant(v)}
                                        disabled={avail <= 0}
                                        divider
                                        sx={{
                                            borderRadius: 1,
                                            '&:hover': { bgcolor: 'action.hover' },
                                        }}
                                    >
                                        <ListItemText
                                            primary={<Typography fontWeight="medium">{v.name}</Typography>}
                                            secondary={
                                                <Box component="span" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.3 }}>
                                                    <Chip label={`SKU: ${v.sku || '—'}`} size="small" variant="outlined" />
                                                    <Chip label={fmtMoney(v.price)} size="small" color="primary" />
                                                    <Chip
                                                        label={avail > 0 ? t('orders_ext.available', { count: avail }) : t('orders_ext.outOfStock')}
                                                        size="small"
                                                        color={avail > 0 ? 'success' : 'error'}
                                                    />
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" disabled={avail <= 0} onClick={() => selectVariant(v)}>
                                                <AddIcon color={avail > 0 ? 'primary' : 'disabled'} />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVariantDialog({ open: false, product: null, variants: [] })}>
                        {t('common.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default OrderForm;
